import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { AutoStoriesRounded, OpenInNewRounded, PlayArrowRounded } from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import { courseTemplateApi } from '../../../lib/api/course-template.api';
import { SyllabusContent } from '../../portal-v2/pages/CourseSyllabusPage';
import { ResyncSyllabusButton } from './ResyncSyllabusButton';
import { GenerateSyllabusWizard } from './GenerateSyllabusWizard';
import { SyllabusOverrideDialogs, type SyllabusEditSection } from './SyllabusOverrideDialogs';
import { errorText } from '../../../lib/error-text';

interface Props {
  sectionId: string;
  /** Refreshes the cockpit structure after a resync/generation materializes modules. */
  onStructureChanged?: () => Promise<void> | void;
}

interface LinkInfo {
  /** The section's syllabus doc, when generated. */
  syllabus: { templateId: string; templateVersion: number } | null;
  /** The linked template's live doc (title + current version). */
  template: { id: string; title: string; version: number } | null;
  courseName: string;
  /** Templates matching the section's course — CTA candidates when no syllabus exists. */
  candidates: { id: string; title: string }[];
}

async function loadLinkInfo(tenantId: string, sectionId: string): Promise<LinkInfo> {
  const syllabiSnap = await getDocs(
    query(
      collection(db, `tenants/${tenantId}/syllabi`),
      where('sectionId', '==', sectionId),
      limit(1),
    ),
  );
  const syllabusData = syllabiSnap.empty
    ? null
    : (syllabiSnap.docs[0]!.data() as Record<string, unknown>);
  const syllabus = syllabusData
    ? {
        templateId: syllabusData['templateId'] as string,
        templateVersion: (syllabusData['templateVersion'] as number) ?? 1,
      }
    : null;

  const sectionSnap = await getDoc(doc(db, `tenants/${tenantId}/sections/${sectionId}`));
  const courseId = sectionSnap.exists() ? (sectionSnap.data()['courseId'] as string) : undefined;
  let courseName = '';
  if (courseId) {
    const courseSnap = await getDoc(doc(db, `tenants/${tenantId}/courses/${courseId}`));
    if (courseSnap.exists()) courseName = (courseSnap.data()['name'] as string) ?? '';
  }

  let template: LinkInfo['template'] = null;
  if (syllabus?.templateId) {
    const tplSnap = await getDoc(
      doc(db, `tenants/${tenantId}/courseTemplates/${syllabus.templateId}`),
    );
    if (tplSnap.exists()) {
      const d = tplSnap.data();
      template = {
        id: tplSnap.id,
        title: (d['title'] as string) ?? '',
        version: (d['version'] as number) ?? 1,
      };
    }
  }

  let candidates: LinkInfo['candidates'] = [];
  if (!syllabus && courseId) {
    const tplSnap = await getDocs(
      query(
        collection(db, `tenants/${tenantId}/courseTemplates`),
        where('courseId', '==', courseId),
      ),
    );
    candidates = tplSnap.docs
      .filter((d) => !d.data()['deletedAt'])
      .map((d) => ({ id: d.id, title: (d.data()['title'] as string) ?? '' }));
  }

  return { syllabus, template, courseName, candidates };
}

export function SyllabusAdminTab({ sectionId, onStructureChanged }: Props): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editSection, setEditSection] = useState<SyllabusEditSection | null>(null);

  const linkQ = useQuery({
    queryKey: ['curso-syllabus-link', currentTenantId, sectionId],
    enabled: !!currentTenantId && !!sectionId,
    queryFn: () => loadLinkInfo(currentTenantId!, sectionId),
  });

  const hasSyllabus = !!linkQ.data?.syllabus;
  const composedQ = useQuery({
    queryKey: ['curso-syllabus-doc', currentTenantId, sectionId],
    enabled: !!currentTenantId && !!sectionId && hasSyllabus,
    queryFn: () => courseTemplateApi.getSectionSyllabus({ tenantId: currentTenantId!, sectionId }),
    retry: false,
  });

  const refreshAll = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['curso-syllabus-link', currentTenantId] }),
      queryClient.invalidateQueries({ queryKey: ['curso-syllabus-doc', currentTenantId] }),
      queryClient.invalidateQueries({ queryKey: ['section-sessions', currentTenantId] }),
    ]);
    await onStructureChanged?.();
  };

  if (linkQ.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (linkQ.isError) {
    return <Alert severity="error">{errorText(linkQ.error)}</Alert>;
  }
  const link = linkQ.data!;

  // ── Empty state: no syllabus generated for this section yet ────────────────
  if (!link.syllabus) {
    const single = link.candidates.length === 1 ? link.candidates[0]! : null;
    return (
      <>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
          <AutoStoriesRounded sx={{ fontSize: 36, color: 'text.disabled' }} />
          <Typography variant="h6" mt={1}>
            {t('lms.syllabusTab.emptyTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5} mb={2}>
            {t('lms.syllabusTab.emptyBody')}
          </Typography>
          {single ? (
            <Button
              variant="contained"
              startIcon={<PlayArrowRounded />}
              onClick={() => setWizardOpen(true)}
            >
              {t('lms.syllabusTab.generateCta', { template: single.title })}
            </Button>
          ) : (
            <Stack spacing={1.5} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {link.candidates.length === 0
                  ? t('lms.syllabusTab.noTemplate')
                  : t('lms.syllabusTab.multipleTemplates')}
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/plantillas-syllabus')}>
                {t('lms.syllabusTab.goToTemplates')}
              </Button>
            </Stack>
          )}
        </Paper>
        {wizardOpen && single && (
          <GenerateSyllabusWizard
            template={single}
            initialSectionId={sectionId}
            onClose={() => setWizardOpen(false)}
            onGenerated={() => {
              setWizardOpen(false);
              void refreshAll();
            }}
          />
        )}
      </>
    );
  }

  // ── Linked state: header (template + versions + resync) + composed doc ─────
  const generatedVersion = link.syllabus.templateVersion;
  const currentVersion = link.template?.version ?? generatedVersion;
  const outdated = currentVersion > generatedVersion;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2" color="text.secondary">
              {t('lms.syllabusTab.linkedTemplate')}:
            </Typography>
            <Typography variant="subtitle2">{link.template?.title ?? '—'}</Typography>
            <Chip
              size="small"
              label={t('lms.syllabusTab.generatedVersion', { version: generatedVersion })}
            />
            {outdated ? (
              <Chip
                size="small"
                color="warning"
                label={t('lms.syllabusTab.changesPending', { version: currentVersion })}
              />
            ) : (
              <Chip size="small" color="success" label={t('lms.syllabusTab.upToDate')} />
            )}
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <ResyncSyllabusButton sectionId={sectionId} onResynced={() => void refreshAll()} />
            <Button
              size="small"
              endIcon={<OpenInNewRounded />}
              onClick={() => navigate('/plantillas-syllabus')}
            >
              {t('lms.syllabusTab.openTemplate')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {composedQ.isLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      )}
      {composedQ.isError && <Alert severity="error">{errorText(composedQ.error)}</Alert>}
      {composedQ.data && (
        <>
          <SyllabusContent
            data={composedQ.data}
            courseName={link.courseName || (link.template?.title ?? '')}
            tenantId={currentTenantId}
            scheduleHref={`/curso/${sectionId}?tab=cronograma`}
            onEditSection={setEditSection}
          />
          <SyllabusOverrideDialogs
            open={editSection}
            onOpenSection={setEditSection}
            sectionId={sectionId}
            data={composedQ.data}
            {...(link.syllabus?.templateId ? { templateId: link.syllabus.templateId } : {})}
            sectionLabel={link.courseName || (link.template?.title ?? '')}
            onClose={() => setEditSection(null)}
            onSaved={() => {
              void queryClient.invalidateQueries({
                queryKey: ['curso-syllabus-doc', currentTenantId],
              });
            }}
          />
        </>
      )}
    </Stack>
  );
}
